import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ErrorState, Spinner } from "@/components/ui";
import { formatDate } from "@/lib/format";
import { ApiError } from "@/lib/http";
import { legal, type ReadablePolicy } from "@/lib/legal";
import { loadPdf, type PdfDocument } from "@/lib/pdf";
import { sanitizePolicyHtml } from "@/lib/sanitize";

const quantise = (w: number) => Math.floor(w / 16) * 16;

/**
 * Shows a published policy: sanitised HTML, or PDF pages drawn to canvases. There is deliberately
 * no download, print or embedded-viewer path. `onLoaded` fires once, when the first page (or the
 * HTML) has actually been put on screen.
 */
export function PolicyViewer({
  versionId,
  orgId,
  onLoaded,
  headingLevel = 2,
}: {
  versionId: string;
  orgId: string | null;
  onLoaded?: () => void;
  headingLevel?: 1 | 2;
}) {
  const policy = useQuery({
    queryKey: ["legal", "version", versionId, orgId],
    queryFn: () => legal.version(versionId, orgId),
  });
  const firedFor = useRef<string | null>(null);
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
  });
  const markLoaded = useCallback(() => {
    if (firedFor.current === versionId) return;
    firedFor.current = versionId;
    onLoadedRef.current?.();
  }, [versionId]);

  if (policy.isPending) return <Spinner />;
  if (policy.isError) {
    if (policy.error instanceof ApiError && policy.error.status === 404)
      return <p className="text-sm text-fg-muted">This policy isn&apos;t available.</p>;
    return <ErrorState error={policy.error} onRetry={() => void policy.refetch()} />;
  }

  const p = policy.data;
  const Heading = headingLevel === 1 ? "h1" : "h2";
  return (
    <article className="w-full min-w-0 space-y-4">
      <header>
        <Heading className={headingLevel === 1 ? "text-2xl font-semibold" : "text-lg font-semibold"}>
          {p.title}
        </Heading>
        <p className="text-sm text-fg-muted">
          v{p.version_number} · Published {formatDate(p.published_at)}
        </p>
      </header>
      {p.format === "pdf" ? (
        <PdfPages versionId={versionId} orgId={orgId} onReady={markLoaded} />
      ) : (
        <PolicyHtml policy={p} onReady={markLoaded} />
      )}
    </article>
  );
}

function PolicyHtml({ policy, onReady }: { policy: ReadablePolicy; onReady: () => void }) {
  const html = useMemo(() => sanitizePolicyHtml(policy.html ?? ""), [policy.html]);
  const empty = html.trim() === "";
  useEffect(() => {
    if (!empty) onReady();
  }, [empty, onReady]);
  if (empty) return <p className="text-sm text-fg-muted">This policy can&apos;t be displayed.</p>;
  return (
    <div
      className="text-sm leading-relaxed text-fg [&_a]:underline [&_h1]:mt-6 [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mt-4 [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-3 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_td]:border [&_td]:border-border [&_td]:p-2 [&_th]:border [&_th]:border-border [&_th]:p-2 [&_ul]:list-disc [&_ul]:pl-5"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function PdfPages({
  versionId,
  orgId,
  onReady,
}: {
  versionId: string;
  orgId: string | null;
  onReady: () => void;
}) {
  const file = useQuery({
    queryKey: ["legal", "file", versionId, orgId],
    queryFn: () => legal.file(versionId, orgId),
    staleTime: Infinity,
  });
  const [attempt, setAttempt] = useState(0);
  const [doc, setDoc] = useState<PdfDocument | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const data = file.data;

  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    let loaded: PdfDocument | null = null;
    loadPdf(data)
      .then((d) => {
        if (cancelled) return d.destroy();
        loaded = d;
        setDoc(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setFailure(e ?? new Error("The policy could not be loaded"));
      });
    return () => {
      cancelled = true;
      loaded?.destroy();
      setDoc(null);
    };
  }, [data, attempt]);

  const shown = Boolean(data) && !failure;
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setWidth(quantise(el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [shown]);

  const fail = useCallback((e: unknown) => setFailure(e ?? new Error("The policy could not be drawn")), []);
  const onPage = useCallback(
    (n: number) => {
      if (n === 1) onReady();
    },
    [onReady],
  );

  if (failure || file.isError) {
    return (
      <ErrorState
        error={failure ?? file.error}
        onRetry={() => {
          setFailure(null);
          setAttempt((a) => a + 1);
          if (file.isError) void file.refetch();
        }}
      />
    );
  }
  if (file.isPending) return <Spinner />;
  return (
    <div ref={box} className="flex w-full flex-col items-center gap-4 select-none">
      {doc &&
        width > 0 &&
        Array.from({ length: doc.numPages }, (_, i) => (
          <PageCanvas
            key={i}
            doc={doc}
            n={i + 1}
            total={doc.numPages}
            width={width}
            onError={fail}
            onReady={onPage}
          />
        ))}
    </div>
  );
}

function PageCanvas({
  doc,
  n,
  total,
  width,
  onError,
  onReady,
}: {
  doc: PdfDocument;
  n: number;
  total: number;
  width: number;
  onError: (e: unknown) => void;
  onReady: (n: number) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let stopped = false;
    const job = doc.renderPage(n, canvas, width);
    job.done.then(
      () => {
        if (!stopped) onReady(n);
      },
      (e: unknown) => {
        if (!stopped) onError(e);
      },
    );
    return () => {
      // a resize or unmount: stop this draw before the canvas is reused
      stopped = true;
      job.cancel();
    };
  }, [doc, n, width, onError, onReady]);
  return (
    <canvas
      ref={ref}
      role="img"
      aria-label={`Page ${n} of ${total}`}
      className="max-w-full bg-white shadow-sm"
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}
